/*
 * What's in the Kit — tab switching for the kit contents card.
 *
 * The card renders every kit server-side and hides all but one, so the list is
 * in the HTML for search engines and for anyone with JS off; this only moves
 * the visible panel.
 *
 * Cards marked `data-kit-follow-variant` also track the enrolment picker: the
 * class hero and the theme's variant pickers both announce a change as a
 * document-level `variant:change` with `detail.variant`, so a shopper switching
 * from "Class Pass + Kit" to "Deluxe Kit" sees the matching kit without having
 * to find the tab. Clicking a tab does NOT change the variant — the card is
 * there to be read, and silently repricing the page from it would be a trap.
 *
 * An option with no kit of its own — "Class Pass Only" — hides the whole card
 * instead of leaving the previous option's kit sitting under it, which read as
 * a promise of products that option does not ship. The markup stays put, so
 * switching back is instant.
 *
 * The snippet is rendered once per block, so the script tag can appear more
 * than once on a page; the guard below keeps it to a single set of listeners.
 */
(function () {
  if (window.__kitContentsInit) return;
  window.__kitContentsInit = true;

  /* The block wrapper carries the merchant's margins, so hiding the card alone
     would leave that spacing behind as a gap. Hide both. */
  var setHidden = function (card, hidden) {
    var block = card.closest('.kitc-block');
    card.hidden = hidden;
    if (block) block.hidden = hidden;
  };

  var select = function (card, index) {
    var tabs = card.querySelectorAll('[data-kit-tab]');
    var panels = card.querySelectorAll('[data-kit-panel]');
    if (!panels.length) return;

    panels.forEach(function (panel) {
      panel.hidden = panel.getAttribute('data-kit-panel') !== String(index);
    });

    tabs.forEach(function (tab) {
      var on = tab.getAttribute('data-kit-tab') === String(index);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.tabIndex = on ? 0 : -1;
    });
  };

  document.addEventListener('click', function (event) {
    var tab = event.target.closest('[data-kit-tab]');
    if (!tab) return;
    var card = tab.closest('[data-kit-contents]');
    if (!card) return;
    select(card, tab.getAttribute('data-kit-tab'));
    tab.focus();
  });

  /* Left/Right move between tabs, Home/End jump to the ends — the roving
     tabindex above is only half of the tablist pattern without this. */
  document.addEventListener('keydown', function (event) {
    var keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
    if (keys.indexOf(event.key) === -1) return;

    var tab = event.target.closest('[data-kit-tab]');
    if (!tab) return;
    var card = tab.closest('[data-kit-contents]');
    if (!card) return;

    var tabs = Array.prototype.slice.call(card.querySelectorAll('[data-kit-tab]'));
    var here = tabs.indexOf(tab);
    if (here === -1) return;

    var next = here;
    if (event.key === 'ArrowLeft') next = (here - 1 + tabs.length) % tabs.length;
    if (event.key === 'ArrowRight') next = (here + 1) % tabs.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = tabs.length - 1;

    event.preventDefault();
    select(card, tabs[next].getAttribute('data-kit-tab'));
    tabs[next].focus();
  });

  document.addEventListener('variant:change', function (event) {
    var variant = event.detail && event.detail.variant;
    if (!variant || !variant.id) return;

    document.querySelectorAll('[data-kit-follow-variant]').forEach(function (card) {
      var panel = card.querySelector('[data-kit-panel][data-kit-variant="' + variant.id + '"]');

      if (panel) {
        select(card, panel.getAttribute('data-kit-panel'));
        setHidden(card, false);
        return;
      }

      /* No panel means this option ships no kit — e.g. "Class Pass Only". The
         theme editor keeps the card up regardless, or the block could not be
         edited from the option it is hidden on. */
      if (!card.hasAttribute('data-kit-keep-empty')) setHidden(card, true);
    });
  });

  /* Liquid can only put `hidden` on the card itself — it has no way to reach
     the wrapper above it. Carry it up once, before first paint. */
  document.querySelectorAll('[data-kit-contents][hidden]').forEach(function (card) {
    setHidden(card, true);
  });
})();
